/**
 * PosteingangKiPanel – KI-Empfehlung, KI-Analyse-Ergebnis und Athena-Chat
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, Loader2, Check, X, ChevronRight,
  MessageSquare, ArrowRight, User, Building2, Brain, Zap, TrendingUp
} from 'lucide-react';

export default function PosteingangKiPanel({
  kiEmpfehlung,
  setKiEmpfehlung,
  loadingKiEmpfehlung,
  analyseResult,
  applyKiEmpfehlung,
  openNeuePersonDialog,
  showDocChat,
  setShowDocChat,
  docChatInput,
  setDocChatInput,
  docChatMessages,
  docChatLoading,
  sendDocChatMessage,
  // Lernmodus
  athenaLernt = false,
  neuesMuster = null,
  setNeuesMuster = () => {},
  athenaStats = null
}) {

  const aktionLabel = {
    'aufgabe_anlegen': 'Aufgabe anlegen',
    'massnahme_zuordnen': 'Maßnahme zuordnen',
    'zur_akte': 'Zur Akte legen',
    'weiterleiten': 'Weiterleiten',
    'ignorieren': 'Ignorieren',
  };

  return (
    <>
      {/* Lernmodus-Indikator */}
      {(athenaLernt || neuesMuster || athenaStats) && (
        <div className={`rounded-lg border p-3 transition-all duration-500 ${
          athenaLernt
            ? 'bg-gradient-to-r from-violet-950 to-indigo-950 border-violet-500/40'
            : neuesMuster
            ? 'bg-gradient-to-r from-green-950 to-emerald-950 border-green-500/40'
            : 'bg-slate-800/60 border-slate-700'
        }`} data-testid="athena-lernmodus-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className={`w-4 h-4 ${athenaLernt ? 'text-violet-400 animate-pulse' : neuesMuster ? 'text-green-400' : 'text-slate-400'}`} />
              <span className={`text-xs font-medium ${athenaLernt ? 'text-violet-300' : neuesMuster ? 'text-green-300' : 'text-slate-400'}`}>
                {athenaLernt ? 'Zeo lernt...' : neuesMuster ? 'Neues Muster erkannt!' : 'Zeo'}
              </span>
              {athenaLernt && (
                <span className="flex gap-0.5">
                  <span className="w-1 h-1 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-1 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1 h-1 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              )}
            </div>
            {athenaStats && !athenaLernt && !neuesMuster && (
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {athenaStats.gelernte_muster} Muster
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  {athenaStats.gedaechtnis_eintraege} Einträge
                </span>
              </div>
            )}
          </div>

          {/* Neues Muster erkannt */}
          {neuesMuster && (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-green-200">{neuesMuster.nachricht}</p>
              <p className="text-xs text-green-400">{neuesMuster.vorschlag}</p>
              <div className="flex items-center gap-2 pt-1">
                <Badge className="bg-green-800/60 text-green-200 text-xs border border-green-600/40">
                  {neuesMuster.anzahl_beobachtungen}× {aktionLabel[neuesMuster.aktion] || neuesMuster.aktion}
                </Badge>
                {neuesMuster.bedingung_wert && (
                  <Badge className="bg-slate-700 text-slate-300 text-xs">
                    {neuesMuster.bedingung_wert}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" className="bg-green-700 hover:bg-green-600 text-white text-xs h-7"
                  onClick={() => setNeuesMuster(null)}
                  data-testid="muster-bestaetigen-btn"
                >
                  <Check className="w-3 h-3 mr-1" /> Verstanden
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-400 text-xs h-7"
                  onClick={() => setNeuesMuster(null)}
                >
                  Schließen
                </Button>
              </div>
            </div>
          )}

          {/* Lernfortschritt */}
          {athenaLernt && athenaStats && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 bg-violet-900/40 rounded-full h-1">
                <div
                  className="bg-violet-400 h-1 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min(100, (athenaStats.lernfortschritt || 0) * 100)}%` }}
                />
              </div>
              <span className="text-xs text-violet-400">
                {Math.round((athenaStats.lernfortschritt || 0) * 100)}%
              </span>
            </div>
          )}
        </div>
      )}

      {/* KI-Empfehlung vom Lern-System */}
      {loadingKiEmpfehlung ? (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-indigo-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">KI analysiert Dokument...</span>
          </div>
        </div>
      ) : kiEmpfehlung?.ki_empfehlung ? (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-indigo-700 flex items-center gap-1">
              <Sparkles className="w-4 h-4" /> KI-Empfehlung
            </p>
            {kiEmpfehlung.automatisierung_moeglich && (
              <Badge className="bg-green-100 text-green-700">Gelernte Regel</Badge>
            )}
          </div>

          {kiEmpfehlung.ki_empfehlung.bezueglich_vorschlag && (
            <div className={`p-2 rounded text-sm ${
              kiEmpfehlung.ki_empfehlung.bezueglich_vorschlag === 'bueroorganisation'
                ? 'bg-blue-100 text-blue-700'
                : kiEmpfehlung.ki_empfehlung.bezueglich_vorschlag === 'klient'
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              <span className="font-medium">Für:</span>{' '}
              {kiEmpfehlung.ki_empfehlung.bezueglich_vorschlag === 'bueroorganisation' ? (
                <span>Büroorganisation</span>
              ) : kiEmpfehlung.ki_empfehlung.bezueglich_vorschlag === 'klient' && kiEmpfehlung.ki_empfehlung.gefundener_klient ? (
                <span>{kiEmpfehlung.ki_empfehlung.gefundener_klient.vorname} {kiEmpfehlung.ki_empfehlung.gefundener_klient.nachname} (bekannter Klient)</span>
              ) : kiEmpfehlung.ki_empfehlung.bezueglich_vorschlag === 'neue_person' && kiEmpfehlung.ki_empfehlung.person_zum_anlegen ? (
                <span
                  className="cursor-pointer underline hover:text-amber-900 transition-colors"
                  onClick={() => openNeuePersonDialog?.(kiEmpfehlung.ki_empfehlung.person_zum_anlegen)}
                  data-testid="ki-neue-person-link"
                >+ {kiEmpfehlung.ki_empfehlung.person_zum_anlegen} (neue Person)</span>
              ) : null}
            </div>
          )}

          {kiEmpfehlung.ki_empfehlung.absender_erkannt && (
            <p className="text-sm text-slate-600">
              <span className="font-medium">Absender:</span> {kiEmpfehlung.ki_empfehlung.absender_erkannt}
            </p>
          )}

          <p className="text-sm text-slate-600">
            <span className="font-medium">Empfehlung:</span>{' '}
            <span className={`font-semibold ${
              kiEmpfehlung.ki_empfehlung.empfohlene_aktion === 'aufgabe_anlegen' ? 'text-amber-600' :
              kiEmpfehlung.ki_empfehlung.empfohlene_aktion === 'zur_akte' ? 'text-green-600' :
              'text-blue-600'
            }`}>
              {kiEmpfehlung.ki_empfehlung.empfohlene_aktion === 'aufgabe_anlegen' ? 'Aufgabe anlegen' :
               kiEmpfehlung.ki_empfehlung.empfohlene_aktion === 'zur_akte' ? 'Zur Akte legen' :
               kiEmpfehlung.ki_empfehlung.empfohlene_aktion === 'weiterleiten' ? 'Weiterleiten' :
               kiEmpfehlung.ki_empfehlung.empfohlene_aktion}
            </span>
          </p>

          {kiEmpfehlung.ki_empfehlung.begruendung && (
            <p className="text-xs text-slate-500 italic">
              {kiEmpfehlung.ki_empfehlung.begruendung}
            </p>
          )}

          {kiEmpfehlung.ki_empfehlung.aufgabe_vorschlag && (
            <div className="bg-white/50 rounded p-2 text-sm">
              <p className="font-medium text-slate-700">
                {kiEmpfehlung.ki_empfehlung.aufgabe_vorschlag.titel}
              </p>
              {kiEmpfehlung.ki_empfehlung.aufgabe_vorschlag.beschreibung && (
                <p className="text-xs text-slate-500 mt-1">
                  {kiEmpfehlung.ki_empfehlung.aufgabe_vorschlag.beschreibung}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button size="sm" onClick={applyKiEmpfehlung} className="flex-1">
              <Check className="w-3 h-3 mr-1" /> Empfehlung übernehmen
            </Button>
            <Button size="sm" variant="outline" onClick={() => setKiEmpfehlung(null)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : null}

      {/* KI-Analyse Ergebnis */}
      {analyseResult && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
          <p className="text-xs font-medium text-purple-700 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> KI-Analyse
            {analyseResult.match_confidence > 0 && (
              <span className="ml-auto text-purple-600">
                {analyseResult.match_confidence}% Konfidenz
              </span>
            )}
          </p>
          {analyseResult.zusammenfassung && (
            <p className="text-sm text-purple-900">{analyseResult.zusammenfassung}</p>
          )}
          {analyseResult.erkannter_klient_name && (
            <div className="flex items-center gap-2 p-2 bg-green-100 rounded text-sm text-green-800">
              <Check className="w-4 h-4" />
              <span>Erkannt: <strong>{analyseResult.erkannter_klient_name}</strong></span>
              {analyseResult.vorgeschlagene_aufgabe_titel && (
                <span className="ml-2">→ <strong>{analyseResult.vorgeschlagene_aufgabe_titel}</strong></span>
              )}
            </div>
          )}
          {analyseResult.entities?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {analyseResult.entities.map((e, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {e.typ === 'person' && <User className="w-3 h-3 mr-1" />}
                  {e.typ === 'institution' && <Building2 className="w-3 h-3 mr-1" />}
                  {e.name || `${e.vorname} ${e.nachname}`}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Kontextbezogener KI-Chat (Athena fragen) */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <button
          onClick={() => setShowDocChat(!showDocChat)}
          className="w-full px-3 py-2 flex items-center justify-between text-white hover:bg-slate-700 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            Zeo fragen
          </span>
          <ChevronRight className={`w-4 h-4 transition-transform ${showDocChat ? 'rotate-90' : ''}`} />
        </button>

        {showDocChat && (
          <div className="border-t border-slate-700">
            {docChatMessages.length > 0 && (
              <div className="max-h-48 overflow-y-auto p-3 space-y-2">
                {docChatMessages.map((msg, i) => (
                  <div key={i} className={`text-sm ${msg.role === 'user' ? 'text-blue-300' : 'text-slate-200'}`}>
                    <span className="font-medium">{msg.role === 'user' ? 'Sie: ' : 'Zeo: '}</span>
                    <span className="whitespace-pre-wrap">{msg.text}</span>
                  </div>
                ))}
                {docChatLoading && (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Zeo denkt...
                  </div>
                )}
              </div>
            )}

            <div className="px-3 py-2 flex flex-wrap gap-1 border-t border-slate-700/50">
              <button
                onClick={() => setDocChatInput('Wer ist das? Welche Person betrifft dieses Dokument?')}
                className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
              >
                Wer ist das?
              </button>
              <button
                onClick={() => setDocChatInput('Lege die Person aus dem Dokument als Betreuten an')}
                className="px-2 py-1 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors"
              >
                Als Betreuten anlegen
              </button>
              <button
                onClick={() => setDocChatInput('Erstelle eine Aufgabe für dieses Dokument')}
                className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
              >
                Aufgabe anlegen
              </button>
            </div>

            <div className="p-3 pt-1 flex gap-2">
              <Input
                value={docChatInput}
                onChange={e => setDocChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendDocChatMessage()}
                placeholder="z.B. 'Lege Herr Müller als Betreuten an'"
                className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-sm"
                disabled={docChatLoading}
              />
              <Button
                size="sm"
                onClick={sendDocChatMessage}
                disabled={!docChatInput.trim() || docChatLoading}
                className="bg-blue-600 hover:bg-blue-500"
              >
                {docChatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
